import React from 'react';
import keyBinder from 'shared/lib/keyBinder';
import CuesheetHelper from 'flux/helpers/cuesheetHelper';
import MessageConstants from 'shared/constants/Messages';
import RouteActions from 'shared/flux/actions/RouteActions';
import { Button } from 'bootstrap';

/**
 * CuesheetMixin, for mixing with cuesheet components
 */
export default {
  contextTypes: {
    router: React.PropTypes.object.isRequired,
  },

  /**
   * Helper for getting the cuesheet state
   */
  _getCueState() {
    return this.state.cuesheet || {};
  },

  /**
   * Helper for getting a cuesheet state field
   */
  _getCueStateField(field) {
    return this._getCueState().get(field);
  },

  /**
   * Get the column width for a column
   */
  _getColumnWidth(width) {
    return CuesheetHelper.getColumnWidth(width);
  },

  /**
   * Get the column width of the segement cell
   */
  _getItemColumnSegmentWidth() {
    const settings = this._getCueStateField('settings');

    return CuesheetHelper.getItemColumnSegmentWidth(settings);
  },

  _getFirstItem(state) {
    state = state || this._getCueState();
    const rowSpec = this._getRows(state).get(0);
    return this._getRowItem(rowSpec, state);
  },

  _getFirstItemNumber(state) {
    let num = parseInt(this._getFirstItem(state).get('number'), 10);
    if (isNaN(num)) num = 100;
    return num;
  },

  getItemNumberAdjusted(item, rowIndex, state) {
    state = state || this._getCueState();
    const show = state.get('show');

    const firstItemNumber = this._getFirstItemNumber(state);
    return show.get('autoCountCues')
      ? firstItemNumber + rowIndex
      : item.get('number');
  },

  _getRowItem(rowSpec, state) {
    state = state || this._getCueState();

    return state.get('rowMap').getIn(rowSpec.get('path'));
  },

  _getRowTagsByRowIndex(rowIndex) {
    const globalElements = this.state.globalElements;
    const rowSpec = this._getCueState().getIn(['rows', rowIndex]);
    const item = this._getCueState().get('rowMap').getIn(rowSpec.get('path'));

    return item
      .get('labels')
      .map((labelId) => {
        return globalElements.getIn(['tags', labelId]);
      })
      .filter((tag) => tag);
  },

  _getRowTagsByRowId(rowId) {
    const { cuesheet, globalElements } = this.state;

    const path = [cuesheet.get('itemSetId'), 'rows', rowId, 'item'];
    const item = this._getCueState().get('rowMap').getIn(path);

    return item.get('labels').map((labelId) => {
      return globalElements.getIn(['tags', labelId]);
    });
  },

  _getRowId(rowSpec) {
    const rowPath = rowSpec.get('path'),
      rowId = rowPath ? rowPath[2] : null;

    return rowId;
  },

  getLoadFailureNode(height = 1200) {
    const errorCode = this._getCueStateField('loadFailureCode');

    if (!errorCode) return null;

    let message = MessageConstants.showLoadFailedMessage;

    if (errorCode === 403) {
      message = MessageConstants.showLoadFailedForbiddenMessage;
    }

    if (errorCode === 404) {
      message = (
        <span>
          {MessageConstants.showLoadFailed404Message}
          <br />
          <br />
          <Button
            bsSize="large"
            className="btn-primary-action"
            onClick={() => {
              RouteActions.goToEvent({
                teamId: this._getCueStateField('teamId'),
                eventId: this._getCueStateField('eventId'),
              });
            }}
          >
            Take me back to the event dash
          </Button>
        </span>
      );
    }

    return (
      <div className="grid-load-failure" style={{ height }}>
        {message}
      </div>
    );
  },

  /**
   * Check to see if cell is selected
   *
   * @param rowIndex
   * @param cellIdentifier - This will be the columnId if the cell type
   * is Data or will be the cell type {Item, Note, etc.} if it is not Data type.
   */
  isCellCopied(rowIndex, cellIdentifier) {
    const state = this._getCueState(),
      rowPath = this._getRows().getIn([rowIndex, 'path']),
      rowId = rowPath ? rowPath[2] : null;

    if (
      state.get('pastableCellParams') &&
      state.get('showId') !== state.getIn(['pastableCellParams', 'showId'])
    )
      return false;
    /**
     * Why are we checking for the same showId?
     *
     * This is to prevent copied state from being carried accross shows.
     * Duplicated shows have the same column Id's so this is why we have to also
     * compare show Ids.
     */
    return state.getIn(['pastableCells', `${rowId}_${cellIdentifier}`])
      ? true
      : false;
  },

  componentDidUpdate(prevProps, prevState) {
    prevState = prevState || {};
    const state = this.state || {};
    const prev = prevState.cuesheet,
      curr = state.cuesheet;

    if (!curr && !prev) return;

    /**
     * Sync the keyscope coming from the store
     */
    this._syncKeyScope(prev, curr);
  },

  /**
   * The keyscope may change depending on what's happened in the store..for example a cell became editable
   */
  _syncKeyScope(prevState, currState) {
    if (prevState.get('keyScope') !== currState.get('keyScope')) {
      keyBinder.setScope(currState.get('keyScope'));
    }
  },

  /**
   * Get the height of a row
   */
  _rowHeightGetter(indx) {
    // Return row height for footer
    if (this._isFooterRow(indx)) return this._footerRowHeightGetter();

    const rowSpec = this._rowGetter(indx);
    //NOTE: on occasion the rowHeightGetter tries to get an index that has been deconsted. When this happens, returning 0 for the height is the best option it seems.
    // Otherwise we run into errors because we get an undefined item object
    if (!rowSpec) return 0;

    const rowType = rowSpec.get('type');

    const item = this._getRowItem(rowSpec);

    const rowHeight = this._getRowHeight(item, rowType, rowSpec.get('path')[2]);

    return rowHeight;
  },

  /**
   * Get the row info for a row position
   *
   */
  _rowGetter(indx) {
    //Return empty object for footer row
    if (this._isFooterRow(indx)) return {};

    const rows = this._getRows();

    // in the event that there are no rows in state, return null
    // this can happen whenever the cuesheet state is cleared. For example,
    // when refreshing rows after an internet disconnect
    if (rows && rows.get) {
      return rows.get(indx);
    }

    return null;
  },

  _getRows(state) {
    state = state || this._getCueState();
    return state.get('rows');
  },

  /**
   * Determine if footer row should be used.
   *
   * Description: One additional index was added to the rowCount in fdt table
   * options. This additional index is for the footer row.
   *
   * If indx is greater then total count of rows then footer row should be used.
   */
  _isFooterRow(indx) {
    const rows = this._getRows();

    if (!rows) return;

    return rows.size <= indx ? true : false;
  },

  /**
   * Get height for footer row.
   */
  _footerRowHeightGetter() {
    return 70;
  },

  /**
   * Get common cell styles used for calculating cell heights.
   */
  _getCommonCellHeightCalcStyles() {
    return {
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
    };
  },

  /**
   * Get cell styles used for calculating data and notes cell heights.
   */
  _getCellHeightCalcStyles() {
    const base = this._getCommonCellHeightCalcStyles();
    return base;
  },

  /**
   * Get fontSize className for cell format
   */
  _getFormattedCellClassName(format) {
    let className = 'cs-cell';

    if (format) {
      if (format.get('fontSize')) {
        className += ' cs-cell-format-fontSize-' + format.get('fontSize');
      }

      if (format.get('bold')) {
        className += ' cs-cell-format-bold';
      }
    } else {
      /**
       * Adds default cell styles if there isn't any cell formatting
       *
       * fontSize: 14px
       * lineHeight: 18px
       */
      className += ' cs-cell-format-fontSize-normal';
    }

    return className;
  },
};
